import { motion, useMotionTemplate, useMotionValue } from 'framer-motion'
import { useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import ArticleCover from './ArticleCover'
import { articleSlug, type Article } from '../../data/articles'
import { useT, useLocale } from '../../i18n/LocaleContext'
import { localizedPath } from '../../i18n/routes'
import { formatDateLocalized } from '../../lib/localizeHtml'

export default function BlogFeatured({ article }: { article: Article }) {
  const { t } = useT()
  const locale = useLocale()
  const meta = article.i18n[locale]
  const [hovered, setHovered] = useState(false)
  const mouseX = useMotionValue(-500)
  const mouseY = useMotionValue(-500)

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - r.left)
    mouseY.set(e.clientY - r.top)
  }
  const onLeave = () => {
    setHovered(false)
    mouseX.set(-500)
    mouseY.set(-500)
  }

  const glow = useMotionTemplate`radial-gradient(600px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.22), transparent 70%)`

  return (
    <section className="relative max-w-6xl mx-auto px-6 pb-20">
      <div className="flex items-end justify-between mb-6">
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold"
        >
          {t('blog.featured')}
        </motion.h2>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        onMouseEnter={() => setHovered(true)}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="group relative"
      >
        <motion.div
          aria-hidden
          style={{ background: glow }}
          className="pointer-events-none absolute -inset-4 rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl -z-10"
        />

        <Link
          to={localizedPath('blog.article', locale, { slug: articleSlug(article, locale) })}
          className="relative grid md:grid-cols-[1.2fr_1fr] gap-0 rounded-3xl border border-ink-100 bg-ink-100 overflow-hidden transition-all duration-500 group-hover:border-accent-200 group-hover:shadow-[0_40px_120px_-40px_rgba(59,93,255,0.4)]"
        >
          {/* Cover */}
          <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[440px] overflow-hidden">
            <motion.div
              animate={{ scale: hovered ? 1.05 : 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              <ArticleCover cover={article.cover} hovered={hovered} className="w-full h-full" />
            </motion.div>

            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <span className="inline-flex items-center rounded-full bg-ink-100 text-ink-900 text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1">
                {t('blog.categories.' + article.category)}
              </span>
              <span className="inline-flex items-center rounded-full bg-accent-700 text-white text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1">
                {t('blog.featured')}
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="relative p-8 sm:p-12 flex flex-col justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs text-ink-500">
                <span>{formatDateLocalized(article.date, locale)}</span>
                <span className="text-ink-300">·</span>
                <span>{t('blog.readTime', { n: article.readTime })}</span>
              </div>

              <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-[-0.02em] leading-[1.1] text-ink-950 group-hover:text-accent-700 transition-colors duration-300">
                {meta.title}
              </h2>

              <p className="mt-5 text-ink-600 leading-relaxed">{meta.excerpt}</p>
            </div>

            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-accent-600">
                <span className="relative">
                  {t('blog.readArticle')}
                  <motion.span
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-0.5 h-[1.5px] bg-accent-600 origin-left"
                    initial={false}
                    animate={{ scaleX: hovered ? 1 : 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  />
                </span>
                <motion.svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  animate={{ x: hovered ? 6 : 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </motion.svg>
              </span>

              <motion.span
                animate={{ scale: hovered ? 1.05 : 1 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-700 text-white shadow-[0_14px_40px_-12px_rgba(59,93,255,0.7)]"
              >
                <motion.svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  animate={{ x: hovered ? 2 : 0, y: hovered ? -2 : 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <path d="M7 17L17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
                </motion.svg>
              </motion.span>
            </div>
          </div>
        </Link>
      </motion.div>
    </section>
  )
}
